import React, { useState, useEffect, useCallback } from "react"
import { Col, Container, Row } from "reactstrap"
import { useStaticQuery, graphql } from "gatsby"
import { MdClose } from "react-icons/md"
import styled from "styled-components"
import vars from "styles/variables"
import Button from "components/common/Button"
import ContactBarForm from "components/ui/ContactBarForm"

function ContactBar({ isNotFixed, isHomePage }) {
  const [showEnquiryForm, setShowEnquiryForm] = useState(false)
  const [showBrochureForm, setShowBrochureForm] = useState(false)
  // Homepage: contact bar starts hidden, appears on scroll
  const [revealed, setRevealed] = useState(false)
  const [transitionReady, setTransitionReady] = useState(false)

  const handleScroll = useCallback(() => {
    if (typeof window === "undefined") return
    if (window.scrollY > 50) {
      setRevealed(true)
      setTransitionReady(true)
    } else {
      setRevealed(false)
    }
  }, [])

  useEffect(() => {
    if (!isHomePage) {
      setRevealed(false)
      setTransitionReady(false)
      return
    }
    if (typeof window !== "undefined" && window.scrollY > 50) {
      setRevealed(true)
      setTransitionReady(true)
    } else {
      setRevealed(false)
      setTransitionReady(false)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isHomePage, handleScroll])
  const data = useStaticQuery(graphql`
    query ContactBarQuery {
      wp {
        inspiredGlobalOptions {
          globalOptions {
            inspiredOptionsContactsPhone
          }
        }
      }
    }
  `)

  const { inspiredOptionsContactsPhone: tel } =
    data.wp.inspiredGlobalOptions.globalOptions || {}

  const isFormOpen = showEnquiryForm || showBrochureForm ? true : false

  const handleFormClose = () => {
    setShowEnquiryForm(false)
    setShowBrochureForm(false)
  }

  const handleFormSuccess = () => {
    handleFormClose()
  }

  const getContactBarTitle = () => {
    if (showEnquiryForm) return ""
    if (showBrochureForm) return ""
    return "Enquire Now"
  }

  const enquiryFormId = 9
  const brochureFormId = 10

  // Tag contact bar form submit buttons with AB test classes for VWO tracking
  useEffect(() => {
    if (!showEnquiryForm && !showBrochureForm) return
    const formId = showEnquiryForm ? enquiryFormId : brochureFormId
    const abClass = showEnquiryForm
      ? "ab-testing-contactbar-enquiry-submit"
      : "ab-testing-contactbar-brochure-submit"
    // Small delay to let the Gravity Form render its button
    const timer = setTimeout(() => {
      const btn = document.getElementById(`gform_submit_button_${formId}`)
      if (btn && !btn.classList.contains(abClass)) {
        btn.classList.add(abClass)
      }
    }, 200)
    return () => clearTimeout(timer)
  }, [showEnquiryForm, showBrochureForm, enquiryFormId, brochureFormId])

  let generalEnquiry = (
    <ContactBarForm
      type={enquiryFormId}
      title="Get your FREE Quote"
      showTrustpilot
      className="white-bg"
      handleFormSuccess={handleFormSuccess}
    />
  )

  let brochure = (
    <ContactBarForm
      type={brochureFormId}
      title="Get a FREE Brochure"
      showTrustpilot
      className="white-bg"
      handleFormSuccess={handleFormSuccess}
    />
  )

  return (
    <StyledContactBar
      $isNotFixed={isNotFixed}
      $isFormOpen={isFormOpen}
      $isHomePage={isHomePage}
      $revealed={revealed}
      $transitionReady={transitionReady}
    >
      <Container className="h-100">
        <Row className="align-items-center h-100">
          <Col>
            <Row className="align-items-center">
              <Col className="contact-bar-title d-md-none d-lg-block">
                <StyledTitle>{getContactBarTitle()}</StyledTitle>
              </Col>
              {isFormOpen && (
                <Col xs="auto" className="d-flex align-items-center">
                  <StyledCloseIcon>
                    <MdClose onClick={handleFormClose} />
                  </StyledCloseIcon>

                  <ClickableStrong onClick={handleFormClose}>
                    Close
                  </ClickableStrong>
                </Col>
              )}
              {!isFormOpen && (
                <Col xs="12" md="auto">
                  <Row>
                    <Col xs="4" className="px-2">
                      <Button
                        className="enquiry-button ab-testing-contactbar-quote-button"
                        onClick={() =>
                          setShowEnquiryForm(() => !showEnquiryForm)
                        }
                      >
                        <span className="d-none d-sm-block">
                          Get a FREE Quote
                        </span>
                        <span
                          className="d-block d-sm-none"
                          style={{ textWrap: "nowrap" }}
                        >
                          FREE Quote
                        </span>
                      </Button>
                    </Col>
                    <Col xs="4" className="pl-1 pr-2">
                      <Button
                        className="enquiry-button ab-testing-contactbar-brochure-button"
                        onClick={() =>
                          setShowBrochureForm(() => !showBrochureForm)
                        }
                      >
                        <span className="d-none d-md-block">
                          Request a Brochure
                        </span>
                        <span className="d-block d-md-none">Brochure</span>
                      </Button>
                    </Col>
                    <Col xs="4" className="pl-1 pr-2">
                      <Button
                        className="enquiry-button ab-testing-contactbar-call-button"
                        to={`tel:${tel}`}
                      >
                        <span className="d-none d-sm-block">Call: {tel}</span>
                        <span className="d-block d-sm-none">Call Us</span>
                      </Button>
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
            {showEnquiryForm && generalEnquiry}
            {showBrochureForm && brochure}
          </Col>
        </Row>
      </Container>
    </StyledContactBar>
  )
}

export default ContactBar

const ClickableStrong = styled.strong`
  cursor: pointer;
`

const StyledContactBar = styled.div`
  position: ${props =>
    props.$isNotFixed && !props.$isFormOpen ? "absolute" : "fixed"};
  background: ${vars.primary};
  color: ${vars.white};
  ${props => (!props.$isNotFixed ? "bottom: 0;" : "top:0;")};
  right: 0;
  width: 100%;
  padding: 1rem 0;
  z-index: 600;
  left: 0;
  height: ${props => (props.$isFormOpen ? "100vh" : "auto")};
  align-items: center;
  ${props => props.$isFormOpen && "overflow-y: scroll;"}
  -webkit-overflow-scrolling:touch;

  /* Homepage: start hidden, slide up on scroll */
  ${props =>
    props.$isHomePage
      ? `
    transform: translateY(${props.$revealed ? "0" : "100%"});
    opacity: ${props.$revealed ? 1 : 0};
    ${
      props.$transitionReady
        ? "transition: transform 0.35s ease, opacity 0.35s ease;"
        : ""
    }
  `
      : ""}

  .col-lg-8 {
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }

  .enquiry-button {
    display: flex;
    justify-content: center;
  }

  @media (max-width: ${vars.screen_sm_max}) {
    ${props => (!props.$isFormOpen ? "" : "top:0;")};
    a,
    button,
    .react-ripples {
      min-width: 100%;
      width: 100%;
      display: block;
    }
  }

  .gform_wrapper {
    margin-left: -15px;
    margin-right: -15px;
  }

  .contact-bar-title {
    @media (max-width: ${vars.screen_sm_max}) {
      ${props => !props.$isFormOpen && "display: none;"}
    }
  }
`
const StyledTitle = styled.span`
  font-size: 1.5rem;
`
const StyledCloseIcon = styled.div`
  height: 32px;
  font-size: 2rem;
  cursor: pointer;
`
